import React, { useEffect, useState, useMemo, useRef } from 'react';
import CustomModal from '../../components/CustomModal/CustumModal';
import { HitApi } from '../../Api/ApiHit';
import { addClosing, searchExpense, updateClosing, addExpense, updateExpense, searchUser } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { calculateMissingAmounts, calculateTotalExpense, formatDateExpense, formatIntlNumber, formatTimestampToDate, getTimestampRange, isObjectEmpty, sumPaymentMethods } from '../../utils/utils';
import { getSelectedUnit } from '../../storage/Storage';
import { setDataAction } from '../../redux/Actions/SetDataAction';
import { SET_EXPENSE } from '../../redux/Reducers/ExpenseReducer';
import { Trash2, Plus, CreditCard, Wallet, Smartphone, Banknote } from 'lucide-react';
import toast from 'react-hot-toast';

function CloseModal({ modal, setModal, isEditable, editStart, editEnd }) {
    const BillingReducer = useSelector((state) => state.BillingReducer);
    const settleMentReducer = useSelector((state) => state.ExpenseReducer);
    const dispatch = useDispatch();
    const [methodData, setMethodData] = useState(null)
    const [expenseData, setExpenseData] = useState(null)
    const [render, setRender] = useState(Date.now())
    const [enteredAmounts, setEnteredAmounts] = useState({
        CASH: '',
        CARD: '',
        UPI: '',
        WALLET: ''
    })
    const [isClosing, setIsClosing] = useState(false)
    const [expenseTableHeight, setExpenseTableHeight] = useState('normal') // 'normal', 'expanded', 'full'
    const [billingTableHeight, setBillingTableHeight] = useState('normal') // 'normal', 'expanded', 'full'
    const expenseTableRef = useRef(null)
    const selectedUnit = getSelectedUnit();
    const [userData, setUserData] = useState(null);
    const [totalSalary, setTotalSalary] = useState(0);

    // Calculate daily utilities
    const calculateDailyUtilities = () => {
        if (!selectedUnit) return { dailyRent: 0, dailyElectricity: 0 };

        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

        const monthlyRent = parseFloat(selectedUnit.rent) || 0;
        const monthlyElectricity = parseFloat(selectedUnit.electricity) || 0;

        return {
            dailyRent: parseFloat((monthlyRent / daysInMonth).toFixed(2)),
            dailyElectricity: parseFloat((monthlyElectricity / daysInMonth).toFixed(2)),
            daysInMonth
        };
    };

    const { dailyRent, dailyElectricity, daysInMonth } = calculateDailyUtilities();

    // Calculate total product cost from all bills
    const calculateProductCost = () => {
        if (!BillingReducer?.billingData || !Array.isArray(BillingReducer.billingData)) {
            return 0;
        }

        let totalProductCost = 0;

        BillingReducer.billingData.forEach((bill) => {
            if (bill.serviceInfo && Array.isArray(bill.serviceInfo)) {
                bill.serviceInfo.forEach((service) => {
                    if (service.serviceData?.steps && Array.isArray(service.serviceData.steps)) {
                        service.serviceData.steps.forEach((step) => {
                            if (step.products && Array.isArray(step.products)) {
                                step.products.forEach((productInfo) => {
                                    const costPrice = parseFloat(productInfo.product?.costPrice) || 0;
                                    const quantity = parseFloat(productInfo.quantity) || 0;
                                    const netQuantity = parseFloat(productInfo.product?.netQuantity) || 1;

                                    // Calculate cost per unit (ml, grams, etc.)
                                    const costPerUnit = costPrice / netQuantity;

                                    // Calculate total cost for used quantity
                                    const productCost = costPerUnit * quantity;

                                    totalProductCost += productCost;
                                });
                            }
                        });
                    }
                });
            }
        });

        return parseFloat(totalProductCost.toFixed(2));
    };

    const totalProductCost = calculateProductCost();

    // Calculate total incentive from all bills
    const calculateTotalIncentive = () => {
        if (!BillingReducer?.billingData || !Array.isArray(BillingReducer.billingData)) {
            return 0;
        }

        return BillingReducer.billingData.reduce((sum, bill) => {
            const subTotal = parseFloat(bill.billSummary?.subTotal) || 0;
            const billIncentive = bill.serviceInfo?.reduce((serviceSum, service) => {
                const incentivePercentage = parseFloat(service.serviceData?.incentive) || 0;
                const incentiveAmount = (subTotal * incentivePercentage) / 100;
                return serviceSum + incentiveAmount;
            }, 0) || 0;
            return sum + billIncentive;
        }, 0);
    };

    const totalIncentive = parseFloat(calculateTotalIncentive().toFixed(2));

    // Fetch users and calculate total salary
    const loadUsers = () => {
        if (!selectedUnit?._id) return;

        const json = {
            page: 1,
            limit: 1000,
            search: {
                unitIds: selectedUnit._id,
                status: true
            }
        };

        HitApi(json, searchUser).then((res) => {
            console.log("User search response:", res);
            if (res?.data) {
                setUserData(res.data);

                // Calculate total salary
                const salary = res.data.reduce((sum, user) => {
                    const userSalary = parseFloat(user.salary) || 0;
                    return sum + userSalary;
                }, 0);

                setTotalSalary(parseFloat(salary.toFixed(2)));
            }
        }).catch((error) => {
            console.error('Error fetching users:', error);
        });
    };

    // Calculate product cost for a specific bill
    const calculateBillProductCost = (bill) => {
        if (!bill.serviceInfo || !Array.isArray(bill.serviceInfo)) {
            return 0;
        }

        let billProductCost = 0;

        bill.serviceInfo.forEach((service) => {
            if (service.serviceData?.steps && Array.isArray(service.serviceData.steps)) {
                service.serviceData.steps.forEach((step) => {
                    if (step.products && Array.isArray(step.products)) {
                        step.products.forEach((productInfo) => {
                            const costPrice = parseFloat(productInfo.product?.costPrice) || 0;
                            const quantity = parseFloat(productInfo.quantity) || 0;
                            const netQuantity = parseFloat(productInfo.product?.netQuantity) || 1;

                            // Calculate cost per unit (ml, grams, etc.)
                            const costPerUnit = costPrice / netQuantity;

                            // Calculate total cost for used quantity
                            const productCost = costPerUnit * quantity;

                            billProductCost += productCost;
                        });
                    }
                });
            }
        });

        return parseFloat(billProductCost.toFixed(2));
    };

    // Load expenses only after billing data is available
    useEffect(() => {
        if (BillingReducer?.billingData && settleMentReducer?.expense === null) {
            loadExpense()
        }
    }, [BillingReducer?.billingData])

    // Auto-generate incentive expenses when both billing data and expenses are loaded
    useEffect(() => {
        console.log('Incentive generation effect triggered');
        console.log('BillingReducer?.billingData:', BillingReducer?.billingData);
        console.log('settleMentReducer?.expense:', settleMentReducer?.expense);

        if (BillingReducer?.billingData && settleMentReducer?.expense && Array.isArray(settleMentReducer.expense)) {
            // Only generate if there are no existing incentive entries (both auto-generated and manual)
            const hasIncentiveEntries = settleMentReducer.expense.some(exp =>
                exp.category === 'Incentive'
            );

            console.log('Has incentive entries:', hasIncentiveEntries);

            if (!hasIncentiveEntries) {
                console.log('Generating incentive expenses...');
                generateIncentiveExpenses();
            }
        }
    }, [BillingReducer?.billingData, settleMentReducer?.expense])

    // Auto-generate utility expenses when expenses are loaded
    useEffect(() => {
        console.log('Utility generation effect triggered');
        console.log('settleMentReducer?.expense:', settleMentReducer?.expense);
        console.log('selectedUnit:', selectedUnit);
        console.log('totalSalary:', totalSalary);
        console.log('userData:', userData);

        if (settleMentReducer?.expense && Array.isArray(settleMentReducer.expense) && selectedUnit) {
            console.log('Generating utility expenses...');
            generateUtilityExpenses();
        }
    }, [settleMentReducer?.expense, selectedUnit, dailyRent, dailyElectricity, totalSalary, userData, totalProductCost])

    // Load users for salary calculation
    useEffect(() => {
        if (selectedUnit?._id && userData === null) {
            loadUsers();
        }
    }, [selectedUnit?._id, userData])

    // Calculate adjusted amount considering existing expenses
    const calculateAdjustedAmount = (method) => {
        const baseAmount = parseFloat(methodData?.[method]) || 0;

        // Get existing expenses for this method (excluding auto-generated)
        const existingExpenses = settleMentReducer?.expense?.filter(exp =>
            exp.mode === method && !exp.isAutoGenerated
        ) || [];

        // Get verified shortage/excess entries with evidence (receipt number)
        const verifiedEntries = settleMentReducer?.expense?.filter(exp =>
            exp.mode === method &&
            (exp.category === 'Shortage' || exp.category === 'Overage') &&
            exp.recieptNumber && exp.recieptNumber.trim() !== ''
        ) || [];

        const totalExpenses = existingExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

        // Apply verified adjustments
        const verifiedAdjustments = verifiedEntries.reduce((sum, exp) => {
            const amount = parseFloat(exp.amount) || 0;
            // If it's a shortage, we had less money, so we adjust upward
            // If it's overage, we had more money, so we adjust downward
            return sum + (exp.category === 'Shortage' ? amount : -amount);
        }, 0);

        return baseAmount - totalExpenses + verifiedAdjustments;
    };

    // Memoize payment method calculations to prevent unnecessary re-renders
    const paymentMethodCalculations = useMemo(() => {
        return ['CASH', 'CARD', 'UPI', 'WALLET'].map((method) => {
            const baseAmount = methodData?.[method] || 0;
            const adjustedAmount = calculateAdjustedAmount(method);

            // Get existing expenses for this method
            const existingExpenses = settleMentReducer?.expense?.filter(exp =>
                exp.mode === method && !exp.isAutoGenerated
            ) || [];
            const totalExpenses = existingExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

            // Get verified adjustments with evidence
            const verifiedEntries = settleMentReducer?.expense?.filter(exp =>
                exp.mode === method &&
                (exp.category === 'Shortage' || exp.category === 'Overage') &&
                exp.recieptNumber && exp.recieptNumber.trim() !== ''
            ) || [];

            const verifiedAdjustments = verifiedEntries.reduce((sum, exp) => {
                const amount = parseFloat(exp.amount) || 0;
                return sum + (exp.category === 'Shortage' ? amount : -amount);
            }, 0);

            return {
                method,
                baseAmount,
                adjustedAmount,
                totalExpenses,
                verifiedEntries,
                verifiedAdjustments
            };
        });
    }, [methodData, settleMentReducer?.expense]);

    const loadExpense = () => {
        const { start, end } = getTimestampRange();
        const json = {
            page: 1, limit: 10, search: {
                createdAt: { $gte: editStart ? editStart : start, $lte: editEnd ? editEnd : end },
            }
        };
        HitApi(json, searchExpense).then((res) => {
            console.log("res?.data?.docs", res?.data?.docs);
            if (res?.status === 200) {
                var payemnts = sumPaymentMethods(BillingReducer)
                if (res?.data?.docs?.length === 0) {
                    setExpenseData({ CASH: 0, UPI: 0, WALLET: 0, CARD: 0 })
                    setMethodData(payemnts)
                    dispatch(setDataAction([], SET_EXPENSE));
                } else {
                    calculateTotalExpense(res?.data?.docs, setExpenseData, payemnts, setMethodData, () => { })
                    dispatch(setDataAction(res?.data?.docs, SET_EXPENSE));
                }
            }
        });
    };

    const handleChange = (value, name, index, type) => {
        if (type === 'addmore') {
            console.log('Adding new expense');
            const currentExpenses = settleMentReducer?.expense || [];
            console.log('Current expenses:', currentExpenses);
            const newData = [...currentExpenses, { mode: '', amount: '', recieptNumber: '', remark: '', date: Date.now(), payee: '', category: '' }];
            console.log('New data after adding:', newData);
            dispatch(setDataAction(newData, SET_EXPENSE));
            calculateTotalExpense(newData, setExpenseData, sumPaymentMethods(BillingReducer), setMethodData, () => { })

            // Auto-scroll to bottom after adding new expense
            setTimeout(() => {
                if (expenseTableRef.current) {
                    expenseTableRef.current.scrollTop = expenseTableRef.current.scrollHeight;
                }
            }, 100);
        }
        else if (type === 'delete') {
            const oldData = [...(settleMentReducer?.expense || [])];
            oldData.splice(index, 1);

            dispatch(setDataAction(oldData, SET_EXPENSE));
            calculateTotalExpense(oldData, setExpenseData, sumPaymentMethods(BillingReducer), setMethodData, () => { });
        }
        else {
            var oldData = [...settleMentReducer?.expense]
            const updatedEntry = {
                ...oldData[index],
                [name]: name === 'date' ? new Date(value).getTime() : value
            };

            // Mark as verified if it's a shortage/overage with receipt number evidence
            if ((updatedEntry.category === 'Shortage' || updatedEntry.category === 'Overage') &&
                updatedEntry.recieptNumber && updatedEntry.recieptNumber.trim() !== '') {
                updatedEntry.isVerified = true;
            } else if (name === 'recieptNumber' && (!value || value.trim() === '')) {
                updatedEntry.isVerified = false;
            }

            oldData[index] = updatedEntry;
            dispatch(setDataAction(oldData, SET_EXPENSE));
            calculateTotalExpense(oldData, setExpenseData, sumPaymentMethods(BillingReducer), setMethodData, () => { })
        }
    }

    const getPaymentIcon = (method) => {
        switch (method) {
            case 'CASH': return <Banknote className="w-5 h-5 text-green-600" />;
            case 'CARD': return <CreditCard className="w-5 h-5 text-blue-600" />;
            case 'UPI': return <Smartphone className="w-5 h-5 text-purple-600" />;
            case 'WALLET': return <Wallet className="w-5 h-5 text-orange-600" />;
            default: return null;
        }
    };

    const handleAmountChange = (method, enteredAmount) => {
        setEnteredAmounts(prev => ({
            ...prev,
            [method]: enteredAmount
        }));

        // Always remove existing automatic entries for this method first
        const filteredExpenses = settleMentReducer?.expense?.filter(exp =>
            !exp.isAutoGenerated || exp.relatedMethod !== method
        ) || [];

        // If the input is empty or invalid, just remove the auto-generated entries and return
        if (!enteredAmount || enteredAmount.trim() === '' || methodData?.[method] === undefined) {
            dispatch(setDataAction(filteredExpenses, SET_EXPENSE));
            calculateTotalExpense(filteredExpenses, setExpenseData, sumPaymentMethods(BillingReducer), setMethodData, () => { });
            return;
        }

        const adjustedBaseAmount = calculateAdjustedAmount(method);
        const entered = parseFloat(enteredAmount) || 0;
        const difference = entered - adjustedBaseAmount;

        if (difference !== 0) {
            // Add new difference entry
            const newExpense = {
                mode: method,
                amount: Math.abs(difference),
                recieptNumber: '',
                remark: difference > 0 ? `Excess ${method} amount` : `Short ${method} amount`,
                date: Date.now(),
                payee: difference > 0 ? 'Excess Collection' : 'Shortage',
                category: difference > 0 ? 'Overage' : 'Shortage',
                isAutoGenerated: true,
                relatedMethod: method
            };

            const updatedExpenses = [...filteredExpenses, newExpense];
            dispatch(setDataAction(updatedExpenses, SET_EXPENSE));
            calculateTotalExpense(updatedExpenses, setExpenseData, sumPaymentMethods(BillingReducer), setMethodData, () => { });
        } else {
            // If amounts match exactly, just keep the filtered expenses (no auto-generated entry needed)
            dispatch(setDataAction(filteredExpenses, SET_EXPENSE));
            calculateTotalExpense(filteredExpenses, setExpenseData, sumPaymentMethods(BillingReducer), setMethodData, () => { });
        }
    };

    const generateIncentiveExpenses = () => {
        console.log('=== Starting generateIncentiveExpenses ===');
        console.log('BillingReducer?.billingData:', BillingReducer?.billingData);

        if (!BillingReducer?.billingData || !Array.isArray(BillingReducer.billingData)) {
            console.log('No billing data or not array, returning');
            return;
        }

        const currentExpenses = [...(settleMentReducer?.expense || [])];
        console.log('Current expenses:', currentExpenses);

        // Keep all existing expenses (don't remove any incentive entries)
        const filteredExpenses = [...currentExpenses];

        const newIncentiveExpenses = [];

        // Generate new incentive entries for each bill
        BillingReducer.billingData.forEach((bill, billIndex) => {
            console.log(`Processing bill ${billIndex}:`, bill);

            if (!bill.serviceInfo || !Array.isArray(bill.serviceInfo)) {
                console.log(`Bill ${billIndex} has no service info or not array`);
                return;
            }

            bill.serviceInfo.forEach((service, serviceIndex) => {
                console.log(`Processing service ${serviceIndex}:`, service);

                const subTotal = parseFloat(bill.billSummary?.subTotal) || 0;
                const incentivePercentage = parseFloat(service.serviceData?.incentive) || 0;
                const incentiveAmount = (subTotal * incentivePercentage) / 100;

                console.log(`SubTotal: ${subTotal}, Incentive %: ${incentivePercentage}, Amount: ${incentiveAmount}`);
                console.log(`Staff data:`, service.staffData);

                if (incentiveAmount > 0 && service.staffData?.name) {
                    // Check if incentive entry already exists (both auto-generated and manual)
                    // Check by staff name, category, and similar amount to avoid duplicates
                    const existingEntry = filteredExpenses.find(exp =>
                        exp.category === 'Incentive' &&
                        exp.payee === service.staffData.name &&
                        (
                            // Check for exact bill/service match (auto-generated)
                            (exp.relatedBillId === (bill._id || `bill-${billIndex}`) &&
                             exp.relatedServiceId === (service.serviceData?._id || `service-${serviceIndex}`)) ||
                            // Check for similar amount (manual entry)
                            Math.abs(parseFloat(exp.amount) - incentiveAmount) < 0.01
                        )
                    );

                    if (!existingEntry) {
                        const incentiveExpense = {
                            mode: 'CASH',
                            amount: incentiveAmount,
                            recieptNumber: `BILL-${bill.billNo || billIndex + 1}`,
                            remark: `Incentive for ${service.serviceData?.name || 'Service'} - ${incentivePercentage}% of ₹${subTotal}`,
                            date: Date.now(),
                            payee: service.staffData.name,
                            category: 'Incentive',
                            isAutoGenerated: true,
                            relatedBillId: bill._id || `bill-${billIndex}`,
                            relatedServiceId: service.serviceData?._id || `service-${serviceIndex}`,
                            staffInfo: {
                                name: service.staffData.name,
                                phoneNumber: service.staffData.phoneNumber,
                                id: service.staffData._id
                            }
                        };

                        console.log('Creating incentive expense:', incentiveExpense);
                        newIncentiveExpenses.push(incentiveExpense);
                    } else {
                        console.log('Existing entry found, skipping');
                    }
                } else {
                    console.log('Skipping: incentiveAmount <= 0 or no staff name');
                }
            });
        });

        console.log('New incentive expenses to add:', newIncentiveExpenses);

        // Only update if there are new incentive expenses to add
        if (newIncentiveExpenses.length > 0) {
            const updatedExpenses = [...filteredExpenses, ...newIncentiveExpenses];
            console.log('Updating expenses with:', updatedExpenses);
            dispatch(setDataAction(updatedExpenses, SET_EXPENSE));
            calculateTotalExpense(updatedExpenses, setExpenseData, sumPaymentMethods(BillingReducer), setMethodData, () => { });
        } else {
            console.log('No new incentive expenses to add');
        }
    };

    const generateUtilityExpenses = () => {
        console.log('=== Starting generateUtilityExpenses ===');
        console.log('totalSalary:', totalSalary);
        console.log('userData:', userData);
        console.log('selectedUnit:', selectedUnit);

        if (!selectedUnit) {
            console.log('No selected unit, returning');
            return;
        }

        const currentExpenses = [...(settleMentReducer?.expense || [])];
        console.log('Current expenses:', currentExpenses);

        // Check what entries already exist (both auto-generated and manual)
        const existingRent = currentExpenses.some(exp => exp.category === 'Rent');
        const existingElectricity = currentExpenses.some(exp => exp.category === 'Electricity');
        const existingSalary = currentExpenses.some(exp => exp.category === 'Salary');
        const existingProducts = currentExpenses.some(exp => exp.category === 'Products');

        console.log('Existing entries:', { existingRent, existingElectricity, existingSalary, existingProducts });

        const newUtilityExpenses = [];
        const today = new Date();
        const currentDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format

        // Only generate entries that don't already exist and have valid data

        // Generate rent expense if rent value exists and not already present
        if (selectedUnit.rent && dailyRent > 0 && !existingRent) {
            const rentExpense = {
                mode: 'CASH',
                amount: dailyRent,
                recieptNumber: `RENT-${currentDate}`,
                remark: `Daily rent expense (₹${selectedUnit.rent}/month ÷ ${daysInMonth} days)`,
                date: Date.now(),
                payee: 'Rent Provider',
                category: 'Rent',
                isAutoGenerated: true,
                relatedCategory: 'utility'
            };
            newUtilityExpenses.push(rentExpense);
            console.log('Adding rent expense:', rentExpense);
        }

        // Generate electricity expense if electricity value exists and not already present
        if (selectedUnit.electricity && dailyElectricity > 0 && !existingElectricity) {
            const electricityExpense = {
                mode: 'CASH',
                amount: dailyElectricity,
                recieptNumber: `ELECTRICITY-${currentDate}`,
                remark: `Daily electricity expense (₹${selectedUnit.electricity}/month ÷ ${daysInMonth} days)`,
                date: Date.now(),
                payee: 'Electricity Provider',
                category: 'Electricity',
                isAutoGenerated: true,
                relatedCategory: 'utility'
            };
            newUtilityExpenses.push(electricityExpense);
            console.log('Adding electricity expense:', electricityExpense);
        }

        // Generate salary expense if salary data exists and not already present
        if (totalSalary > 0 && userData && userData.length > 0 && !existingSalary) {
            const dailySalary = parseFloat((totalSalary / 30).toFixed(2));
            const salaryExpense = {
                mode: 'CASH',
                amount: dailySalary,
                recieptNumber: `SALARY-${currentDate}`,
                remark: `Daily salary expense for ${userData.length} employees (₹${totalSalary}/month ÷ 30 days)`,
                date: Date.now(),
                payee: 'Staff Payroll',
                category: 'Salary',
                isAutoGenerated: true,
                relatedCategory: 'operational'
            };
            newUtilityExpenses.push(salaryExpense);
            console.log('Adding salary expense:', salaryExpense);
        } else {
            console.log('Salary not added - totalSalary:', totalSalary, 'userData:', userData?.length, 'existingSalary:', existingSalary);
        }

        // Generate product cost expense if product costs exist and not already present
        if (totalProductCost > 0 && !existingProducts) {
            const productExpense = {
                mode: 'CASH',
                amount: parseFloat(totalProductCost.toFixed(2)),
                recieptNumber: `PRODUCTS-${currentDate}`,
                remark: `Product costs for services provided today`,
                date: Date.now(),
                payee: 'Product Suppliers',
                category: 'Products',
                isAutoGenerated: true,
                relatedCategory: 'operational'
            };
            newUtilityExpenses.push(productExpense);
            console.log('Adding product expense:', productExpense);
        }

        console.log('New operational expenses to add:', newUtilityExpenses);

        // Only update if there are actually new expenses to add
        if (newUtilityExpenses.length > 0) {
            const updatedExpenses = [...currentExpenses, ...newUtilityExpenses];
            console.log('Updating expenses with utilities:', updatedExpenses);
            dispatch(setDataAction(updatedExpenses, SET_EXPENSE));
            calculateTotalExpense(updatedExpenses, setExpenseData, sumPaymentMethods(BillingReducer), setMethodData, () => { });
        } else {
            console.log('No new utility expenses to add - all already exist or no valid data');
        }
    };

    const handleCloseBook = async () => {
        setIsClosing(true);
        try {
            // Process all expenses
            const expenses = settleMentReducer?.expense || [];
            console.log('Processing expenses for close book:', expenses);

            for (const expense of expenses) {
                // Skip completely empty expenses (all fields empty)
                const isEmpty = !expense.mode && !expense.amount && !expense.recieptNumber &&
                    !expense.remark && !expense.payee && !expense.category;

                if (isEmpty) {
                    console.log('Skipping empty expense:', expense);
                    continue;
                }

                if (expense._id) {
                    // Update existing expense
                    const updateData = { _id: expense._id, ...expense };
                    console.log('Updating expense:', updateData);
                    const updateResponse = await HitApi(updateData, updateExpense);
                    console.log('Update response:', updateResponse);
                } else {
                    // Add new expense (including blank fields with some data)
                    console.log('Adding new expense:', expense);
                    const addResponse = await HitApi(expense, addExpense);
                    console.log('Add response:', addResponse);
                }
            }

            // Create closing entry after all expenses are submitted
            await createClosingEntry();

            // Close the modal after successful processing
            setTimeout(() => {
                setModal(false);
                setIsClosing(false);
            }, 1000);

        } catch (error) {
            console.error('Error processing expenses:', error);
            setIsClosing(false);
        }
    };

    // Function to create closing entry using CreateClosingDto
    const createClosingEntry = async () => {
        try {
            // Calculate payment method totals
            const totalSales = BillingReducer?.billingData?.reduce((sum, bill) =>
                sum + (parseFloat(bill.billSummary?.grandTotal) || 0), 0) || 0;

            const totalExpenses = settleMentReducer?.expense?.reduce((sum, exp) =>
                sum + (parseFloat(exp.amount) || 0), 0) || 0;

            // Get payment method amounts from methodData (received amounts)
            const upiAmount = parseFloat(methodData?.UPI) || 0;
            const walletAmount = parseFloat(methodData?.WALLET) || 0;
            const cashAmount = parseFloat(methodData?.CASH) || 0;
            const cardAmount = parseFloat(methodData?.CARD) || 0;

            // Create closing entry JSON according to CreateClosingDto
            const closingJson = {
                status: true, // Book is being closed
                upiAmount: upiAmount,
                walletAmount: walletAmount,
                grandTotal: totalSales,
                cashAmount: cashAmount,
                cardAmount: cardAmount,
                expense: totalExpenses,
                incentive: totalIncentive,
                rent: dailyRent,
                electricity: dailyElectricity,
                productCost: totalProductCost,
                sales: totalSales
            };

            console.log('Creating closing entry with data:', closingJson);

            const closingResponse = await HitApi(closingJson, addClosing);
            console.log('Closing entry response:', closingResponse);

            if (closingResponse?.statusCode === 201) {
                console.log('Book closed successfully');
                toast.success("Book closed successfully");
            } else {
                console.error('Failed to close book:', closingResponse);
                toast.error("Failed to close book");
            }

        } catch (error) {
            console.error('Error creating closing entry:', error);
            toast.error("Error closing book");
            throw error; // Re-throw to be caught by handleCloseBook
        }
    };

    console.log('methodData------', methodData);
    console.log('settleMentReducer?.expense------', settleMentReducer?.expense);

    return (
        <CustomModal isOpen={modal} size="full" height={'100vh'} onClose={() => setModal(false)}>
            <div className='bg-gradient-to-br from-gray-50 to-gray-100 min-h-full'>
                {/* Header */}
                <div className='bg-white shadow-sm border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4'>
                    <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3'>
                        <div className='flex-1'>
                            <h2 className='text-base sm:text-lg font-bold text-gray-800 mb-1'>Expense Management</h2>
                            <p className='text-gray-600 text-xs sm:text-sm'>Manage your daily expenses and payment methods</p>
                        </div>
                        <div className='flex gap-2 sm:gap-3 w-full sm:w-auto'>
                            <button
                                onClick={() => setModal(false)}
                                disabled={isClosing}
                                className='flex-1 sm:flex-none bg-gray-600 hover:bg-gray-700 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                                <span className='hidden sm:inline'>Close</span>
                            </button>
                            <button
                                onClick={handleCloseBook}
                                disabled={isClosing}
                                className='flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
                            >
                                {isClosing ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        <span className='hidden sm:inline'>Closing...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                                        </svg>
                                        <span className='hidden sm:inline'>Close Book</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                <div className='p-3 sm:p-6 space-y-4 sm:space-y-6 max-h-[calc(100vh-120px)] overflow-y-auto'>
                    {/* Financial Summary Cards */}
                    <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3'>
                        {/* Total Sales Card */}
                        <div className='bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-2 sm:p-3 border border-green-200'>
                            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
                                <div className='flex-1'>
                                    <h3 className='text-xs font-medium text-green-700 mb-1'>Sales</h3>
                                    <p className='text-sm sm:text-lg font-bold text-green-900 break-all'>
                                        ₹{BillingReducer?.billingData?.reduce((sum, bill) =>
                                            sum + (parseFloat(bill.billSummary?.grandTotal) || 0), 0
                                        ).toFixed(2) || '0.00'}
                                    </p>
                                    <p className='text-xs text-green-600 mt-1'>
                                        {BillingReducer?.billingData?.length || 0} bills
                                    </p>
                                </div>
                                <div className='bg-green-200 p-1 sm:p-2 rounded-full self-end sm:self-auto mt-2 sm:mt-0'>
                                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>


                        {/* Daily Salary Card */}
                        <div className='bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-2 sm:p-3 border border-slate-200'>
                            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
                                <div className='flex-1'>
                                    <h3 className='text-xs font-medium text-slate-700 mb-1'>Daily Salary</h3>
                                    <p className='text-sm sm:text-lg font-bold text-slate-900 break-all'>
                                        ₹{(totalSalary / 30).toFixed(2)}
                                    </p>
                                    <p className='text-xs text-slate-600 mt-1'>
                                        Per day (÷30 days)
                                    </p>
                                </div>
                                <div className='bg-slate-200 p-1 sm:p-2 rounded-full self-end sm:self-auto mt-2 sm:mt-0'>
                                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Total Expenses Card */}
                        <div className='bg-gradient-to-r from-red-50 to-red-100 rounded-lg p-3 border border-red-200'>
                            <div className='flex items-center justify-between'>
                                <div>
                                    <h3 className='text-xs font-medium text-red-700 mb-1'>Expenses</h3>
                                    <p className='text-lg font-bold text-red-900'>
                                        ₹{settleMentReducer?.expense?.reduce((sum, exp) =>
                                            sum + (parseFloat(exp.amount) || 0), 0
                                        ).toFixed(2) || '0.00'}
                                    </p>
                                    <p className='text-xs text-red-600 mt-1'>
                                        {settleMentReducer?.expense?.length || 0} entries
                                    </p>
                                </div>
                                <div className='bg-red-200 p-2 rounded-full'>
                                    <svg className="w-4 h-4 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4m16 0l-4 4m4-4l-4-4"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Net Revenue Card */}
                        <div className='bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200'>
                            <div className='flex items-center justify-between'>
                                <div>
                                    <h3 className='text-xs font-medium text-blue-700 mb-1'>Net Revenue</h3>
                                    <p className='text-lg font-bold text-blue-900'>
                                        ₹{(
                                            (BillingReducer?.billingData?.reduce((sum, bill) =>
                                                sum + (parseFloat(bill.billSummary?.grandTotal) || 0), 0
                                            ) || 0) -
                                            (settleMentReducer?.expense?.reduce((sum, exp) =>
                                                sum + (parseFloat(exp.amount) || 0), 0
                                            ) || 0)
                                        ).toFixed(2)}
                                    </p>
                                    <p className='text-xs text-blue-600 mt-1'>
                                        Sales - Expenses
                                    </p>
                                </div>
                                <div className='bg-blue-200 p-2 rounded-full'>
                                    <svg className="w-4 h-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Today's Rent Card */}
                        <div className='bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-3 border border-purple-200'>
                            <div className='flex items-center justify-between'>
                                <div>
                                    <h3 className='text-xs font-medium text-purple-700 mb-1'>Rent</h3>
                                    <p className='text-lg font-bold text-purple-900'>
                                        ₹{dailyRent.toFixed(2)}
                                    </p>
                                    <p className='text-xs text-purple-600 mt-1'>
                                        Daily rent
                                    </p>
                                </div>
                                <div className='bg-purple-200 p-2 rounded-full'>
                                    <svg className="w-4 h-4 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Today's Electricity Card */}
                        <div className='bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-3 border border-yellow-200'>
                            <div className='flex items-center justify-between'>
                                <div>
                                    <h3 className='text-xs font-medium text-yellow-700 mb-1'>Electricity</h3>
                                    <p className='text-lg font-bold text-yellow-900'>
                                        ₹{dailyElectricity.toFixed(2)}
                                    </p>
                                    <p className='text-xs text-yellow-600 mt-1'>
                                        Daily electricity
                                    </p>
                                </div>
                                <div className='bg-yellow-200 p-2 rounded-full'>
                                    <svg className="w-4 h-4 text-yellow-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Product Cost Card */}
                        <div className='bg-gradient-to-r from-teal-50 to-teal-100 rounded-lg p-3 border border-teal-200'>
                            <div className='flex items-center justify-between'>
                                <div>
                                    <h3 className='text-xs font-medium text-teal-700 mb-1'>Products</h3>
                                    <p className='text-lg font-bold text-teal-900'>
                                        ₹{totalProductCost.toFixed(2)}
                                    </p>
                                    <p className='text-xs text-teal-600 mt-1'>
                                        Product costs
                                    </p>
                                </div>
                                <div className='bg-teal-200 p-2 rounded-full'>
                                    <svg className="w-4 h-4 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Total Incentive Card */}
                        <div className='bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-3 border border-orange-200'>
                            <div className='flex items-center justify-between'>
                                <div>
                                    <h3 className='text-xs font-medium text-orange-700 mb-1'>Incentives</h3>
                                    <p className='text-lg font-bold text-orange-900'>
                                        ₹{totalIncentive.toFixed(2)}
                                    </p>
                                    <p className='text-xs text-orange-600 mt-1'>
                                        Staff incentives
                                    </p>
                                </div>
                                <div className='bg-orange-200 p-2 rounded-full'>
                                    <svg className="w-4 h-4 text-orange-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment Methods Summary */}
                    <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6'>
                        <h3 className='text-sm sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2'>
                            <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                            Payment Methods Summary
                        </h3>
                        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4'>
                            {paymentMethodCalculations.map((calc) => {
                                const { method, baseAmount, adjustedAmount, totalExpenses, verifiedEntries, verifiedAdjustments } = calc;
                                const enteredAmount = parseFloat(enteredAmounts[method]) || 0;
                                const difference = enteredAmounts[method] ? enteredAmount - adjustedAmount : 0;

                                return (
                                    <div key={method} className='bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-3 sm:p-4 border border-gray-200'>
                                        <div className='flex items-center gap-2 sm:gap-3 mb-2'>
                                            {getPaymentIcon(method)}
                                            <span className='font-medium text-gray-700 text-xs sm:text-sm'>{method}</span>
                                        </div>
                                        <div className='space-y-1 mb-2'>
                                            <div className='text-xs text-gray-600'>
                                                Base: ₹{baseAmount.toFixed(2)}
                                            </div>
                                            {totalExpenses > 0 && (
                                                <div className='text-xs text-red-600'>
                                                    Expenses: -₹{totalExpenses.toFixed(2)}
                                                </div>
                                            )}
                                            {verifiedAdjustments !== 0 && (
                                                <div className={`text-sm ${verifiedAdjustments > 0 ? 'text-green-600' : 'text-blue-600'}`}>
                                                    Verified Adj: {verifiedAdjustments > 0 ? '+' : ''}₹{verifiedAdjustments.toFixed(2)}
                                                    {verifiedEntries.length > 0 && (
                                                        <span className='text-xs ml-1'>({verifiedEntries.length} entries)</span>
                                                    )}
                                                </div>
                                            )}
                                            <div className='text-sm sm:text-lg font-bold text-gray-900 border-t pt-1'>
                                                Expected: ₹{adjustedAmount.toFixed(2)}
                                            </div>
                                        </div>
                                        <input
                                            type="number"
                                            placeholder="Enter amount"
                                            value={enteredAmounts[method]}
                                            onChange={(e) => handleAmountChange(method, e.target.value)}
                                            className='w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm mb-2'
                                        />
                                        {enteredAmounts[method] && difference !== 0 && (
                                            <div className={`text-xs font-medium ${difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {difference > 0 ? '+' : ''}₹{difference.toFixed(2)} {difference > 0 ? 'Excess' : 'Short'}
                                            </div>
                                        )}
                                        {enteredAmounts[method] && difference === 0 && (
                                            <div className="text-xs font-medium text-green-600">
                                                ✓ Balanced
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Expenses Table */}
                    <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
                        <div className='px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2'>
                            <h3 className='text-sm sm:text-lg font-semibold text-gray-800'>Expense Details</h3>
                            <button
                                onClick={() => {
                                    if (expenseTableHeight === 'normal') setExpenseTableHeight('expanded');
                                    else if (expenseTableHeight === 'expanded') setExpenseTableHeight('full');
                                    else setExpenseTableHeight('normal');
                                }}
                                className='flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200'
                                title={
                                    expenseTableHeight === 'normal' ? 'Expand table' :
                                        expenseTableHeight === 'expanded' ? 'Fully expand table' : 'Collapse table'
                                }
                            >
                                {expenseTableHeight === 'normal' ? (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                        Expand
                                    </>
                                ) : expenseTableHeight === 'expanded' ? (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
                                        </svg>
                                        Full View
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                                        </svg>
                                        Collapse
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Table Header - Hidden on mobile, replaced with card layout */}
                        <div className='hidden md:grid grid-cols-8 bg-gray-800 text-white text-sm font-medium'>
                            <div className='px-4 py-3 border-r border-gray-700'>Payment Method</div>
                            <div className='px-4 py-3 border-r border-gray-700'>Receipt No.</div>
                            <div className='px-4 py-3 border-r border-gray-700'>Amount</div>
                            <div className='px-4 py-3 border-r border-gray-700'>Note</div>
                            <div className='px-4 py-3 border-r border-gray-700'>Payee</div>
                            <div className='px-4 py-3 border-r border-gray-700'>Category</div>
                            <div className='px-4 py-3 border-r border-gray-700'>Date</div>
                            <div className='px-4 py-3'>Action</div>
                        </div>

                        {/* Table Body */}
                        <div
                            ref={expenseTableRef}
                            className={`${expenseTableHeight === 'normal' ? 'max-h-80' :
                                expenseTableHeight === 'expanded' ? 'max-h-96' : 'max-h-screen'
                                } overflow-y-auto transition-all duration-300`}
                        >
                            {settleMentReducer?.expense?.length !== 0 ? (
                                settleMentReducer?.expense?.map((ele, i) => (
                                    <div key={i} className={`md:grid md:grid-cols-8 flex flex-col md:flex-row text-black border-b border-gray-100 hover:bg-gray-50 transition-colors p-3 md:p-0 ${
                                        ele.isAutoGenerated && ele.category === 'Incentive' ? 'bg-orange-50 border-orange-200' :
                                        ele.isAutoGenerated && ele.category === 'Rent' ? 'bg-purple-50 border-purple-200' :
                                        ele.isAutoGenerated && ele.category === 'Electricity' ? 'bg-yellow-50 border-yellow-200' :
                                        ele.isAutoGenerated && ele.category === 'Salary' ? 'bg-slate-50 border-slate-200' :
                                        ele.isAutoGenerated && ele.category === 'Products' ? 'bg-teal-50 border-teal-200' :
                                        ele.isAutoGenerated ? 'bg-blue-50 border-blue-200' :
                                        ele.isVerified ? 'bg-green-50 border-green-200' : ''
                                        }`}>
                                        <div className='mb-2 md:mb-0 md:px-2 md:py-3 md:border-r md:border-gray-200'>
                                            <label className='block md:hidden text-xs font-medium text-gray-700 mb-1'>Payment Method</label>
                                            <select
                                                className='w-full px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs md:text-sm'
                                                onChange={(e) => handleChange(e.target.value, 'mode', i)}
                                                value={ele?.mode}
                                                disabled={ele.isAutoGenerated && (ele.category === 'Shortage' || ele.category === 'Overage' || ele.category === 'Rent' || ele.category === 'Electricity' || ele.category === 'Salary' || ele.category === 'Products') || (ele.isAutoGenerated && ele.relatedMethod)}
                                            >
                                                <option value="">Select Mode</option>
                                                <option value="CASH">💵 CASH</option>
                                                <option value="UPI">📱 UPI</option>
                                                <option value="CARD">💳 CARD</option>
                                                <option value="WALLET">👛 WALLET</option>
                                            </select>
                                        </div>
                                        <div className='mb-2 md:mb-0 md:px-2 md:py-3 md:border-r md:border-gray-200'>
                                            <label className='block md:hidden text-xs font-medium text-gray-700 mb-1'>Receipt No.</label>
                                            <input
                                                onChange={(e) => handleChange(e.target.value, 'recieptNumber', i)}
                                                placeholder='Receipt #'
                                                className='w-full px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs md:text-sm'
                                                value={ele?.recieptNumber}
                                                disabled={ele.isAutoGenerated && (ele.category === 'Shortage' || ele.category === 'Overage' || ele.category === 'Rent' || ele.category === 'Electricity' || ele.category === 'Salary' || ele.category === 'Products') || (ele.isAutoGenerated && ele.relatedMethod)}
                                            />
                                        </div>
                                        <div className='mb-2 md:mb-0 md:px-2 md:py-3 md:border-r md:border-gray-200'>
                                            <label className='block md:hidden text-xs font-medium text-gray-700 mb-1'>Amount</label>
                                            <input
                                                onChange={(e) => handleChange(e.target.value, 'amount', i)}
                                                placeholder='₹ Amount'
                                                className='w-full px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs md:text-sm'
                                                value={ele?.amount ? parseFloat(ele.amount).toFixed(2) : ''}
                                                type="number"
                                                disabled={ele.isAutoGenerated && (ele.category === 'Shortage' || ele.category === 'Overage' || ele.category === 'Rent' || ele.category === 'Electricity' || ele.category === 'Salary' || ele.category === 'Products') || (ele.isAutoGenerated && ele.relatedMethod)}
                                            />
                                        </div>
                                        <div className='mb-2 md:mb-0 md:px-2 md:py-3 md:border-r md:border-gray-200'>
                                            <label className='block md:hidden text-xs font-medium text-gray-700 mb-1'>Note</label>
                                            <input
                                                onChange={(e) => handleChange(e.target.value, 'remark', i)}
                                                placeholder='Add note...'
                                                className='w-full px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs md:text-sm'
                                                value={ele?.remark}
                                                disabled={ele.isAutoGenerated && (ele.category === 'Shortage' || ele.category === 'Overage' || ele.category === 'Rent' || ele.category === 'Electricity' || ele.category === 'Salary' || ele.category === 'Products') || (ele.isAutoGenerated && ele.relatedMethod)}
                                            />
                                        </div>
                                        <div className='mb-2 md:mb-0 md:px-2 md:py-3 md:border-r md:border-gray-200'>
                                            <label className='block md:hidden text-xs font-medium text-gray-700 mb-1'>Payee</label>
                                            <input
                                                onChange={(e) => handleChange(e.target.value, 'payee', i)}
                                                placeholder='Payee name'
                                                className='w-full px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs md:text-sm'
                                                value={ele?.payee}
                                                disabled={ele.isAutoGenerated && (ele.category === 'Shortage' || ele.category === 'Overage' || ele.category === 'Rent' || ele.category === 'Electricity' || ele.category === 'Salary' || ele.category === 'Products') || (ele.isAutoGenerated && ele.relatedMethod)}
                                            />
                                        </div>
                                        <div className='mb-2 md:mb-0 md:px-2 md:py-3 md:border-r md:border-gray-200'>
                                            <label className='block md:hidden text-xs font-medium text-gray-700 mb-1'>Category</label>
                                            <input
                                                onChange={(e) => handleChange(e.target.value, 'category', i)}
                                                placeholder='Category'
                                                className='w-full px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs md:text-sm'
                                                value={ele?.category}
                                                disabled={ele.isAutoGenerated && (ele.category === 'Shortage' || ele.category === 'Overage' || ele.category === 'Rent' || ele.category === 'Electricity' || ele.category === 'Salary' || ele.category === 'Products') || (ele.isAutoGenerated && ele.relatedMethod)}
                                            />
                                        </div>
                                        <div className='mb-2 md:mb-0 md:px-2 md:py-3 md:border-r md:border-gray-200'>
                                            <label className='block md:hidden text-xs font-medium text-gray-700 mb-1'>Date</label>
                                            <input
                                                onChange={(e) => handleChange(e.target.value, 'date', i)}
                                                className='w-full px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs md:text-sm'
                                                type='date'
                                                value={formatTimestampToDate(ele?.date)}
                                                disabled={ele.isAutoGenerated && (ele.category === 'Shortage' || ele.category === 'Overage' || ele.category === 'Rent' || ele.category === 'Electricity' || ele.category === 'Salary' || ele.category === 'Products') || (ele.isAutoGenerated && ele.relatedMethod)}
                                            />
                                        </div>
                                        <div className='md:px-2 md:py-3 flex justify-center md:justify-center items-center'>
                                            {ele.isAutoGenerated && ele.category === 'Incentive' ? (
                                                <span className="text-xs text-orange-600 font-medium">Incentive</span>
                                            ) : ele.isAutoGenerated && ele.category === 'Rent' ? (
                                                <span className="text-xs text-purple-600 font-medium">Rent</span>
                                            ) : ele.isAutoGenerated && ele.category === 'Electricity' ? (
                                                <span className="text-xs text-yellow-600 font-medium">Electricity</span>
                                            ) : ele.isAutoGenerated && ele.category === 'Salary' ? (
                                                <span className="text-xs text-slate-600 font-medium">Salary</span>
                                            ) : ele.isAutoGenerated && ele.category === 'Products' ? (
                                                <span className="text-xs text-teal-600 font-medium">Products</span>
                                            ) : ele.isAutoGenerated ? (
                                                <span className="text-xs text-blue-600 font-medium">Auto</span>
                                            ) : (
                                                <button
                                                    onClick={() => handleChange(false, false, i, 'delete')}
                                                    className='p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200 hover:scale-105'
                                                    title="Delete expense"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            )}
                                            {ele.isVerified && (
                                                <span className="text-xs text-green-600 font-medium ml-1">✓ Verified</span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className='px-6 py-12 text-center'>
                                    <div className='flex flex-col items-center gap-3'>
                                        <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center'>
                                            <CreditCard className="w-8 h-8 text-gray-400" />
                                        </div>
                                        <p className='text-gray-500 text-lg font-medium'>No expenses found</p>
                                        <p className='text-gray-400 text-sm'>Add your first expense to get started</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Add More Button */}
                    <div className='flex justify-center sm:justify-end'>
                        <button
                            onClick={() => handleChange(false, false, false, 'addmore')}
                            className='w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium transition-colors duration-200 shadow-md hover:shadow-lg'
                        >
                            <Plus size={16} className="sm:w-5 sm:h-5" />
                            Add New Expense
                        </button>
                    </div>

                    {/* Billing Details Section */}
                    {BillingReducer?.billingData && BillingReducer.billingData.length > 0 && (
                        <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
                            <div className='px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2'>
                                <h3 className='text-sm sm:text-lg font-semibold text-gray-800 flex items-center gap-2'>
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Today's Bills ({BillingReducer.billingData.length})
                                </h3>
                                <button
                                    onClick={() => {
                                        if (billingTableHeight === 'normal') setBillingTableHeight('expanded');
                                        else if (billingTableHeight === 'expanded') setBillingTableHeight('full');
                                        else setBillingTableHeight('normal');
                                    }}
                                    className='flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200'
                                    title={
                                        billingTableHeight === 'normal' ? 'Expand table' :
                                            billingTableHeight === 'expanded' ? 'Fully expand table' : 'Collapse table'
                                    }
                                >
                                    {billingTableHeight === 'normal' ? (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                            </svg>
                                            Expand
                                        </>
                                    ) : billingTableHeight === 'expanded' ? (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
                                            </svg>
                                            Full View
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                                            </svg>
                                            Collapse
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Mobile: Show simple header */}
                            <div className='block sm:hidden bg-gray-800 text-white px-4 py-3'>
                                <div className='text-sm font-medium'>Today's Bills</div>
                            </div>
                            
                            {/* Desktop: Full table header */}
                            <div className='hidden sm:grid grid-cols-11 bg-gray-800 text-white text-sm font-medium'>
                                <div className='px-4 py-3 border-r border-gray-700'>Bill No.</div>
                                <div className='px-4 py-3 border-r border-gray-700'>Client</div>
                                <div className='px-4 py-3 border-r border-gray-700'>Services</div>
                                <div className='px-4 py-3 border-r border-gray-700'>Staff</div>
                                <div className='px-4 py-3 border-r border-gray-700'>Base Amount</div>
                                <div className='px-4 py-3 border-r border-gray-700'>Tax (GST)</div>
                                <div className='px-4 py-3 border-r border-gray-700'>Incentive</div>
                                <div className='px-4 py-3 border-r border-gray-700'>Product Cost</div>
                                <div className='px-4 py-3 border-r border-gray-700'>Payment Method</div>
                                <div className='px-4 py-3 border-r border-gray-700'>Status</div>
                                <div className='px-4 py-3'>Time</div>
                            </div>

                            {/* Billing Table Body */}
                            <div className={`${billingTableHeight === 'normal' ? 'max-h-60' :
                                billingTableHeight === 'expanded' ? 'max-h-80' : 'max-h-screen'
                                } overflow-y-auto transition-all duration-300 overflow-x-auto sm:overflow-x-visible`}>
                                {BillingReducer.billingData.map((bill, index) => {
                                    // Calculate total incentive for this bill (percentage-based on subTotal)
                                    const subTotal = parseFloat(bill.billSummary?.subTotal) || 0;
                                    const totalIncentivePercentage = bill.serviceInfo?.reduce((sum, service) => {
                                        const incentivePercentage = parseFloat(service.serviceData?.incentive) || 0;
                                        return sum + incentivePercentage;
                                    }, 0) || 0;
                                    const totalIncentive = (subTotal * totalIncentivePercentage) / 100;

                                    // Use GST from bill summary as tax amount
                                    const taxAmount = parseFloat(bill.billSummary?.GST) || 0;

                                    return (
                                        <React.Fragment key={bill._id || index}>
                                            {/* Mobile Card Layout */}
                                            <div className='block sm:hidden border-b border-gray-100 hover:bg-gray-50 transition-colors p-4 bg-white rounded-lg mb-2 mx-2 shadow-sm'>
                                                <div className='space-y-3'>
                                                    {/* Header Row - Bill Number and Status */}
                                                    <div className='flex justify-between items-center'>
                                                        <div className='text-sm font-mono font-medium text-gray-800'>
                                                            Bill #{bill.billNo || index + 1}
                                                        </div>
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${bill.billingStatus ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                            {bill.billingStatus ? 'Paid' : 'Pending'}
                                                        </span>
                                                    </div>

                                                    {/* Client Info */}
                                                    <div>
                                                        <div className='text-sm font-medium text-gray-900'>{bill.clientInfo?.name || 'Walk-in Customer'}</div>
                                                        {bill.clientInfo?.phoneNumber && (
                                                            <div className='text-xs text-gray-500'>{bill.clientInfo.phoneNumber}</div>
                                                        )}
                                                    </div>

                                                    {/* Services and Staff */}
                                                    <div className='grid grid-cols-2 gap-3'>
                                                        <div>
                                                            <div className='text-xs font-medium text-gray-500 mb-1'>Services</div>
                                                            {bill.serviceInfo?.length > 0 ? (
                                                                <div>
                                                                    <div className='text-sm font-medium'>{bill.serviceInfo[0].serviceData.name}</div>
                                                                    {bill.serviceInfo.length > 1 && (
                                                                        <div className='text-xs text-gray-500'>+{bill.serviceInfo.length - 1} more</div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className='text-xs text-gray-500'>No services</span>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className='text-xs font-medium text-gray-500 mb-1'>Staff</div>
                                                            {bill.serviceInfo?.length > 0 ? (
                                                                <div>
                                                                    <div className='text-sm font-medium'>{bill.serviceInfo[0].staffData?.name || 'N/A'}</div>
                                                                    {bill.serviceInfo.length > 1 && (
                                                                        <div className='text-xs text-gray-500'>
                                                                            {bill.serviceInfo.slice(1).map((service, idx) =>
                                                                                service.staffData?.name
                                                                            ).filter(Boolean).join(', ') || 'Multiple staff'}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className='text-xs text-gray-500'>No staff</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Financial Details */}
                                                    <div className='grid grid-cols-2 gap-3 pt-2 border-t border-gray-100'>
                                                        <div>
                                                            <div className='text-xs font-medium text-gray-500 mb-1'>Base Amount</div>
                                                            <div className='text-sm font-bold text-green-600'>₹{(bill.billSummary?.subTotal || 0).toFixed(2)}</div>
                                                            {bill.billSummary?.discount > 0 && (
                                                                <div className='text-xs text-red-500'>-₹{bill.billSummary.discount.toFixed(2)} discount</div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className='text-xs font-medium text-gray-500 mb-1'>Tax (GST)</div>
                                                            <div className='text-sm font-bold text-red-600'>₹{taxAmount.toFixed(2)}</div>
                                                            <div className='text-xs text-gray-500'>({bill.billSummary?.gstPercentage || 5}%)</div>
                                                        </div>
                                                    </div>

                                                    {/* Additional Details */}
                                                    <div className='grid grid-cols-2 gap-3'>
                                                        <div>
                                                            <div className='text-xs font-medium text-gray-500 mb-1'>Incentive</div>
                                                            <div className='text-sm font-bold text-orange-600'>
                                                                {totalIncentive > 0 ? `₹${totalIncentive.toFixed(2)}` : '-'}
                                                            </div>
                                                            {totalIncentivePercentage > 0 && (
                                                                <div className='text-xs text-gray-500'>{totalIncentivePercentage}%</div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className='text-xs font-medium text-gray-500 mb-1'>Product Cost</div>
                                                            <div className='text-sm font-bold text-teal-600'>
                                                                ₹{calculateBillProductCost(bill).toFixed(2)}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Payment Methods */}
                                                    <div>
                                                        <div className='text-xs font-medium text-gray-500 mb-2'>Payment Methods</div>
                                                        <div className='flex flex-wrap gap-1'>
                                                            {bill.paymentMethod?.map((payment, idx) => (
                                                                payment.price > 0 && (
                                                                    <span key={idx} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${payment.title === 'CASH' ? 'bg-green-100 text-green-800' :
                                                                        payment.title === 'CARD' ? 'bg-blue-100 text-blue-800' :
                                                                            payment.title === 'UPI' ? 'bg-purple-100 text-purple-800' :
                                                                                payment.title === 'WALLET' ? 'bg-orange-100 text-orange-800' :
                                                                                    'bg-gray-100 text-gray-800'
                                                                        }`}>
                                                                        {payment.title} ₹{payment.price.toFixed(2)}
                                                                    </span>
                                                                )
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Time */}
                                                    <div className='text-right'>
                                                        <div className='text-xs text-gray-500'>
                                                            {bill.createdAt ? new Date(bill.createdAt).toLocaleTimeString('en-IN', {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            }) : 'N/A'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Desktop Table Row */}
                                            <div className='hidden sm:grid grid-cols-11 text-black border-b border-gray-100 hover:bg-gray-50 transition-colors'>
                                                <div className='px-4 py-3 border-r border-gray-200 text-sm font-mono'>
                                                    #{bill.billNo || index + 1}
                                                </div>
                                                <div className='px-4 py-3 border-r border-gray-200'>
                                                    <div className='text-sm font-medium'>{bill.clientInfo?.name || 'Walk-in Customer'}</div>
                                                    {bill.clientInfo?.phoneNumber && (
                                                        <div className='text-xs text-gray-500'>{bill.clientInfo.phoneNumber}</div>
                                                    )}
                                                </div>
                                                <div className='px-4 py-3 border-r border-gray-200'>
                                                    <div className='text-sm'>
                                                        {bill.serviceInfo?.length > 0 ? (
                                                            <div>
                                                                <div className='font-medium'>{bill.serviceInfo[0].serviceData.name}</div>
                                                                {bill.serviceInfo.length > 1 && (
                                                                    <div className='text-xs text-gray-500'>+{bill.serviceInfo.length - 1} more</div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className='text-gray-500'>No services</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className='px-4 py-3 border-r border-gray-200'>
                                                    <div className='text-sm'>
                                                        {bill.serviceInfo?.length > 0 ? (
                                                            <div>
                                                                <div className='font-medium'>{bill.serviceInfo[0].staffData?.name || 'N/A'}</div>
                                                                {bill.serviceInfo[0].staffData?.phoneNumber && (
                                                                    <div className='text-xs text-gray-500'>{bill.serviceInfo[0].staffData.phoneNumber}</div>
                                                                )}
                                                                {bill.serviceInfo.length > 1 && (
                                                                    <div className='text-xs text-gray-500 mt-1'>
                                                                        {bill.serviceInfo.slice(1).map((service, idx) =>
                                                                            service.staffData?.name
                                                                        ).filter(Boolean).join(', ') || 'Multiple staff'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className='text-gray-500'>No staff</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className='px-4 py-3 border-r border-gray-200'>
                                                    <div className='text-sm font-bold text-green-600'>₹{(bill.billSummary?.subTotal || 0).toFixed(2)}</div>
                                                    {bill.billSummary?.discount > 0 && (
                                                        <div className='text-xs text-red-500'>-₹{bill.billSummary.discount.toFixed(2)} discount</div>
                                                    )}
                                                </div>
                                                <div className='px-4 py-3 border-r border-gray-200'>
                                                    <div className='text-sm font-bold text-red-600'>₹{taxAmount.toFixed(2)}</div>
                                                    <div className='text-xs text-gray-500'>GST ({bill.billSummary?.gstPercentage || 5}%)</div>
                                                </div>
                                                <div className='px-4 py-3 border-r border-gray-200'>
                                                    <div className='text-sm font-bold text-orange-600'>
                                                        {totalIncentive > 0 ? `₹${totalIncentive.toFixed(2)}` : '-'}
                                                    </div>
                                                    {totalIncentivePercentage > 0 && (
                                                        <div className='text-xs text-gray-500'>{totalIncentivePercentage}% of base</div>
                                                    )}
                                                </div>
                                                <div className='px-4 py-3 border-r border-gray-200'>
                                                    <div className='text-sm font-bold text-teal-600'>
                                                        ₹{calculateBillProductCost(bill).toFixed(2)}
                                                    </div>
                                                    <div className='text-xs text-gray-500'>Product cost</div>
                                                </div>
                                                <div className='px-4 py-3 border-r border-gray-200'>
                                                    <div className='flex flex-wrap gap-1'>
                                                        {bill.paymentMethod?.map((payment, idx) => (
                                                            payment.price > 0 && (
                                                                <span key={idx} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${payment.title === 'CASH' ? 'bg-green-100 text-green-800' :
                                                                    payment.title === 'CARD' ? 'bg-blue-100 text-blue-800' :
                                                                        payment.title === 'UPI' ? 'bg-purple-100 text-purple-800' :
                                                                            payment.title === 'WALLET' ? 'bg-orange-100 text-orange-800' :
                                                                                'bg-gray-100 text-gray-800'
                                                                    }`}>
                                                                    {payment.title} ₹{payment.price.toFixed(2)}
                                                                </span>
                                                            )
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className='px-4 py-3 border-r border-gray-200'>
                                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${bill.billingStatus ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {bill.billingStatus ? 'Paid' : 'Pending'}
                                                    </span>
                                                </div>
                                                <div className='px-4 py-3 text-xs text-gray-600'>
                                                    {bill.createdAt ? new Date(bill.createdAt).toLocaleTimeString('en-IN', {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) : 'N/A'}
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                            {/* Billing Summary Footer */}
                            <div className='px-3 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-200'>
                                <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-7 gap-2 sm:gap-4 text-xs sm:text-sm'>
                                    <div>
                                        <span className='text-gray-600'>Total Bills:</span>
                                        <span className='font-medium text-gray-800 ml-1'>{BillingReducer.billingData.length}</span>
                                    </div>
                                    <div>
                                        <span className='text-gray-600'>Total Base Amount:</span>
                                        <span className='font-medium text-green-600 ml-1'>
                                            ₹{BillingReducer.billingData.reduce((sum, bill) =>
                                                sum + (parseFloat(bill.billSummary?.subTotal) || 0), 0
                                            ).toFixed(2)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className='text-gray-600'>Total Tax (GST):</span>
                                        <span className='font-medium text-red-600 ml-1'>
                                            ₹{BillingReducer.billingData.reduce((sum, bill) => {
                                                const taxAmount = parseFloat(bill.billSummary?.GST) || 0;
                                                return sum + taxAmount;
                                            }, 0).toFixed(2)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className='text-gray-600'>Total Incentive:</span>
                                        <span className='font-medium text-orange-600 ml-1'>
                                            ₹{BillingReducer.billingData.reduce((sum, bill) => {
                                                const subTotal = parseFloat(bill.billSummary?.subTotal) || 0;
                                                const billIncentive = bill.serviceInfo?.reduce((serviceSum, service) => {
                                                    const incentivePercentage = parseFloat(service.serviceData?.incentive) || 0;
                                                    const incentiveAmount = (subTotal * incentivePercentage) / 100;
                                                    return serviceSum + incentiveAmount;
                                                }, 0) || 0;
                                                return sum + billIncentive;
                                            }, 0).toFixed(2)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className='text-gray-600'>Total Product Cost:</span>
                                        <span className='font-medium text-teal-600 ml-1'>
                                            ₹{BillingReducer.billingData.reduce((sum, bill) => {
                                                return sum + calculateBillProductCost(bill);
                                            }, 0).toFixed(2)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className='text-gray-600'>Total Discount:</span>
                                        <span className='font-medium text-red-600 ml-1'>
                                            ₹{BillingReducer.billingData.reduce((sum, bill) =>
                                                sum + (parseFloat(bill.billSummary?.discount) || 0), 0
                                            ).toFixed(2)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className='text-gray-600'>Total GST:</span>
                                        <span className='font-medium text-blue-600 ml-1'>
                                            ₹{BillingReducer.billingData.reduce((sum, bill) =>
                                                sum + (parseFloat(bill.billSummary?.GST) || 0), 0
                                            ).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </CustomModal>
    );
}

export default CloseModal;
